import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { X, Lock, CheckCircle, ArrowRight } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { API } from './AuthContext';
import { useEntitlements } from './EntitlementsContext';

const UpgradeContext = createContext(null);

/** Reason copy for each paywall trigger. */
const REASONS = {
    snag_cap_reached: {
        headline: 'You\'ve reached 10 defects on Foundation',
        blurb: 'Upgrade to Rectify to log unlimited defects and send the report to your builder.',
    },
    property_cap_reached: {
        headline: 'Unlock to add more properties',
        blurb: 'Unlock your current property for £14.99 and DefectR lifts the one-property cap — add as many as you need, each at £14.99. Or go lifetime with Surveyor.',
    },
    property_locked: {
        headline: 'Unlock this property — £14.99',
        blurb: 'You\'ve already unlocked one property. Each additional home needs its own £14.99 unlock to log defects, send to the builder, and sync replies. Or go lifetime for unlimited properties.',
    },
    share_locked: {
        headline: 'Unlock to send to your builder',
        blurb: 'The shareable link and QR reply portal are part of Rectify.',
    },
    reply_portal_locked: {
        headline: 'Reply portal needs Rectify',
        blurb: 'The QR / text code sync with your builder is part of the paid tier.',
    },
    pdf_cap_reached: {
        headline: 'You\'ve used your free report preview',
        blurb: 'Unlock this property for unlimited PDF exports, share links, and builder replies.',
    },
    floorplan_cap_reached: {
        headline: 'Foundation includes one floorplan',
        blurb: 'Unlock this property to add multiple floors, a garden plan or a separate site map.',
    },
    ai_cap_reached: {
        headline: 'You\'ve used your 3 free AI analyses',
        blurb: 'Unlock this property for unlimited AI verdicts on every defect — severity, likely cause, estimated cost, NHBC references and recommended fixes.',
    },
    walkaround_ai_paid_only: {
        headline: 'Walk-around AI Suggest is a paid feature',
        blurb: 'Rectify (£14.99 per property) and Surveyor (£149.99 lifetime) unlock AI-suggested title + severity on every walk-around photo. Foundation covers manual capture and mark-up on 1 property free.',
    },
    manual: {
        headline: 'Unlock this property',
        blurb: 'Get unlimited defects, floorplans, share links and PDF reports for this home.',
    },
};

export function UpgradeProvider({ children }) {
    const [state, setState] = useState({ open: false, property: null, reason: 'manual' });
    const [loading, setLoading] = useState(false);
    const ent = useEntitlements();

    const show = useCallback((opts) => {
        setState({
            open: true,
            property: opts?.property || null,
            reason: opts?.reason || 'manual',
        });
    }, []);

    const hide = useCallback(() => {
        // Reset loading too, in case the user came back via browser-back
        // from Stripe while we were still redirecting.
        setLoading(false);
        setState(s => ({ ...s, open: false }));
    }, []);

    // Guard against a "stuck spinner" if the user hits browser-back after
    // the Stripe redirect kicked off but before the redirect actually
    // navigated away.
    useEffect(() => {
        const onShow = () => setLoading(false);
        window.addEventListener('pageshow', onShow);
        return () => window.removeEventListener('pageshow', onShow);
    }, []);

    const startCheckout = async (productType = 'property_unlock') => {
        if (productType === 'property_unlock' && !state.property?.id) return;
        setLoading(true);
        try {
            const res = await axios.post(`${API}/billing/checkout-session`, {
                product_type: productType,
                property_id: state.property?.id,
                origin_url: window.location.origin,
            }, { withCredentials: true });
            if (res.data.already_unlocked || res.data.already_unlimited) {
                toast.success('You already have full access.');
                hide();
                return;
            }
            if (!res.data?.url) {
                toast.error('Checkout session could not start. Please try again.');
                setLoading(false);
                return;
            }
            window.location.href = res.data.url;
        } catch (err) {
            const msg = err?.response?.data?.detail || 'Could not start checkout. Please try again.';
            toast.error(typeof msg === 'string' ? msg : 'Checkout failed');
            setLoading(false);
        }
    };

    return (
        <UpgradeContext.Provider value={{ show, hide, ...state }}>
            {children}
            {state.open && (
                <UpgradeModalUI
                    property={state.property}
                    reason={state.reason}
                    loading={loading}
                    demoPayments={!!ent?.demoPayments}
                    onClose={hide}
                    onPay={() => startCheckout('property_unlock')}
                    onGoLifetime={() => startCheckout('lifetime_unlock')}
                />
            )}
        </UpgradeContext.Provider>
    );
}

function UpgradeModalUI({ property, reason, loading, demoPayments, onClose, onPay, onGoLifetime }) {
    const reasonCopy = REASONS[reason] || REASONS.manual;
    return (
        <div className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-4" data-testid="upgrade-modal">
            <div className="bg-white w-full max-w-md border border-[#18181B]">
                <div className="flex items-center justify-between px-4 py-3 border-b border-[#18181B]">
                    <div className="flex items-center gap-2">
                        <Lock size={16} weight="bold" />
                        <span className="font-display font-semibold">Rectify Unlock</span>
                    </div>
                    <button onClick={onClose} data-testid="upgrade-modal-close" className="p-1 hover:bg-[#F4F4F5]">
                        <X size={18} />
                    </button>
                </div>

                <div className="p-5">
                    <h2 className="font-display text-2xl font-bold leading-tight">{reasonCopy.headline}</h2>
                    <p className="mt-2 text-sm text-[#52525B]">{reasonCopy.blurb}</p>

                    {property?.name && (
                        <div className="mt-4 border border-[#E4E4E7] p-3">
                            <div className="label-tiny text-[#52525B]">Unlocking</div>
                            <div className="font-display font-semibold">{property.name}</div>
                            {property.address && <div className="text-xs text-[#52525B]">{property.address}</div>}
                        </div>
                    )}

                    <div className="mt-4 border border-[#1FA5CF] bg-[#E8F5FB] p-3">
                        <div className="flex items-baseline justify-between">
                            <div className="font-display text-3xl font-bold text-[#09090B]">£14.99</div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#52525B]">
                                One-off · No subscription
                            </div>
                        </div>
                        <ul className="mt-3 space-y-1 text-sm">
                            <li className="flex items-center gap-2"><CheckCircle size={14} weight="fill" color="#00875A" />Unlimited defects on this property</li>
                            <li className="flex items-center gap-2"><CheckCircle size={14} weight="fill" color="#00875A" />Unlimited floorplans (multi-floor)</li>
                            <li className="flex items-center gap-2"><CheckCircle size={14} weight="fill" color="#00875A" />Shareable link to your builder</li>
                            <li className="flex items-center gap-2"><CheckCircle size={14} weight="fill" color="#00875A" />Unlimited PDF exports</li>
                            <li className="flex items-center gap-2"><CheckCircle size={14} weight="fill" color="#00875A" />Builder reply portal (QR sync)</li>
                        </ul>
                    </div>

                    <button
                        onClick={onPay}
                        disabled={loading || !property?.id}
                        data-testid="upgrade-modal-pay-btn"
                        className={`mt-4 w-full py-3 font-mono text-xs uppercase tracking-[0.2em] active:scale-95 transition-all duration-150 inline-flex items-center justify-center gap-2 ${loading || !property?.id ? 'bg-[#A1A1AA] text-white cursor-wait' : 'bg-[#1FA5CF] text-white hover:bg-[#1787AB]'}`}
                    >
                        {loading ? 'Redirecting to Stripe…' : (<>Continue to payment <ArrowRight size={16} weight="bold" /></>)}
                    </button>

                    {/* Surveyor (£149.99 lifetime) — secondary CTA. */}
                    <div
                        className="mt-4 border border-dashed border-[#18181B] p-3 bg-[#FAFAFA]"
                        data-testid="upgrade-modal-surveyor-panel"
                    >
                        <div className="flex items-baseline justify-between">
                            <div className="font-display font-semibold">
                                📐 Surveyor — lifetime
                            </div>
                            <div className="font-display text-lg font-bold">£149.99</div>
                        </div>
                        <p className="text-xs text-[#52525B] mt-1">
                            Unlock <strong>every</strong> property you ever create — unlimited defects, floorplans, share links, PDFs. Break-even after 8 properties; perfect for people with property portfolios.
                        </p>
                        <button
                            onClick={onGoLifetime}
                            disabled={loading}
                            data-testid="upgrade-modal-lifetime-btn"
                            className={`mt-2 w-full py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] border active:scale-95 transition-all duration-150 inline-flex items-center justify-center gap-2 ${loading ? 'border-[#A1A1AA] text-[#A1A1AA] cursor-wait' : 'border-[#18181B] hover:bg-[#18181B] hover:text-white'}`}
                        >
                            Go lifetime — £149.99 <ArrowRight size={14} weight="bold" />
                        </button>
                    </div>

                    <button
                        onClick={onClose}
                        data-testid="upgrade-modal-later-btn"
                        className="mt-2 w-full py-3 font-mono text-xs uppercase tracking-[0.2em] border border-[#E4E4E7] hover:bg-[#F4F4F5]"
                    >
                        Maybe later
                    </button>

                    <p className="mt-3 text-[10px] font-mono uppercase tracking-[0.18em] text-[#52525B] text-center">
                        Payment handled by Stripe · No card data touches DefectR
                    </p>
                    <p className="mt-1 text-[10px] text-[#52525B] text-center" data-testid="upgrade-modal-terms-consent">
                        By continuing you agree to our{' '}
                        <Link to="/terms" target="_blank" rel="noreferrer" className="underline hover:text-[#1787AB]">
                            Terms &amp; refunds
                        </Link>.
                    </p>
                    {demoPayments && (
                        <p className="mt-2 text-[10px] font-mono uppercase tracking-[0.18em] text-[#C97A1F] text-center" data-testid="upgrade-modal-demo-banner">
                            🧪 Sandbox — using the shared demo Stripe key · No real payment taken
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}

export const useUpgrade = () => useContext(UpgradeContext);
